import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import Layout from "../components/Layout";
import NextButton from "../components/NextButton";
import type { StepComponentType } from "../../hooks/IncorporationWizardFormContext";
import { useIncorporationWizardFormCtx } from "../../hooks/IncorporationWizardFormContext";
import TextInput from "../../components/TextInput";
import RadioGroup from "../../components/RadioGroup";
import PhoneInput from "../../components/PhoneInput";
import CheckboxInput from "../../components/CheckboxInput";
import { type IncorporationWizardInitialData } from "@/schemas/incorporation";
import {
  createOrUpdateIncorporationRequest,
  IncorporationRequestError,
} from "@/services/incorporationRequest";

// Base schema with always-required fields
const baseSchema = z.object({
  // Persona autorizada - conditionally required (will be validated in refine)
  persona_autorizada_nombre: z.string().optional(),
  // persona_autorizada_email is always required regardless of correspondencia_tipo or checkbox state
  persona_autorizada_email: z
    .string({ error: "field_required" })
    .min(1, "legal_address.authorized.email.required")
    .email("legal_address.authorized.email.invalid"),
  persona_autorizada_telefono: z.string().optional(),
  persona_autorizada_direccion: z.string().optional(),
  persona_autorizada_pais: z.string().optional(),
  persona_autorizada_ciudad: z.string().optional(),
  persona_autorizada_usar_correspondencia: z.boolean().optional(),

  // Always required billing fields
  facturacion_nombre: z
    .string({ error: "field_required" })
    .min(1, "legal_address.billing.name.required"),
  facturacion_atencion: z
    .string({ error: "field_required" })
    .min(1, "legal_address.billing.attention.required"),
});

/**
 * Domicilio - keep fields optional for backward compatibility but not validate
 * @deprecated - use correspondencia_tipo instead
 */
const domicilioSchema = z.object({
  domicilio_proporcionado: z.enum(["cliente", "abogado"]).optional(),
  domicilio_direccion: z.string().optional(),
  domicilio_pais: z.string().optional(),
  domicilio_ciudad: z.string().optional(),
  domicilio_telefono: z.string().optional(),
});

// Correspondencia discriminated union based on correspondencia_tipo
const correspondenciaDigitalSchema = z.object({
  correspondencia_tipo: z.literal("digital"),
  correspondencia_email: z
    .string({ error: "field_required" })
    .min(1, "legal_address.mail.email.required")
    .email("legal_address.mail.email.invalid"),
  // Keep correspondencia boolean for backward compatibility
  correspondencia: z.boolean().optional(),
});

const correspondenciaFisicoSchema = z.object({
  correspondencia_tipo: z.literal("fisico"),
  correspondencia_nombre: z
    .string({ error: "field_required" })
    .min(1, "legal_address.mail.name.required"),
  correspondencia_direccion: z
    .string({ error: "field_required" })
    .min(1, "legal_address.mail.address.required"),
  correspondencia_pais: z
    .string({ error: "field_required" })
    .min(1, "legal_address.mail.country.required"),
  correspondencia_ciudad: z
    .string({ error: "field_required" })
    .min(1, "legal_address.mail.city.required"),
  correspondencia_telefono: z
    .string({ error: "field_required" })
    .min(1, "legal_address.mail.phone.required")
    .e164({ error: "legal_address.mail.phone.invalid" }),
  correspondencia_persona_contacto: z
    .string({ error: "field_required" })
    .min(1, "legal_address.mail.contact_person.required"),
  // Keep correspondencia boolean for backward compatibility
  correspondencia: z.boolean().optional(),
});

const correspondenciaSchema = z.discriminatedUnion("correspondencia_tipo", [
  correspondenciaDigitalSchema,
  correspondenciaFisicoSchema,
]);

// Facturacion discriminated union
const facturacionTrueSchema = z.object({
  facturacion: z.literal(true),
});

const facturacionFalseSchema = z.object({
  facturacion: z.literal(false),
  facturacion_direccion: z
    .string({ error: "field_required" })
    .min(1, "legal_address.billing.address.required"),
  facturacion_pais: z
    .string({ error: "field_required" })
    .min(1, "legal_address.billing.country.required"),
  facturacion_ciudad: z
    .string({ error: "field_required" })
    .min(1, "legal_address.billing.city.required"),
  facturacion_telefono: z
    .string({ error: "field_required" })
    .min(1, "legal_address.billing.phone.required")
    .e164({ error: "legal_address.billing.phone.invalid" }),
});

const facturacionSchema = z.discriminatedUnion("facturacion", [
  facturacionTrueSchema,
  facturacionFalseSchema,
]);

// Combine all schemas using intersection
const legalAddressSchemaBase = baseSchema
  .and(domicilioSchema)
  .and(correspondenciaSchema)
  .and(facturacionSchema);

// Add conditional validation for persona_autorizada fields
// persona_autorizada_email is always required & validated in baseSchema.
// When correspondencia_tipo is "fisico" and persona_autorizada_usar_correspondencia is true,
// the rest of persona_autorizada fields (nombre, telefono, direccion, pais, ciudad) are not required.
const legalAddressSchema = legalAddressSchemaBase.superRefine((data, ctx) => {
  const skipOtherPersonaFields =
    data.correspondencia_tipo === "fisico" &&
    data.persona_autorizada_usar_correspondencia === true;

  // Other persona_autorizada fields: only required when not skipping (digital mode
  // or fisico with checkbox unchecked)
  if (!skipOtherPersonaFields) {
    if (!data.persona_autorizada_nombre || data.persona_autorizada_nombre.trim().length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "legal_address.authorized.name.required",
        path: ["persona_autorizada_nombre"],
      });
    }

    if (!data.persona_autorizada_telefono || data.persona_autorizada_telefono.trim().length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "legal_address.authorized.phone.required",
        path: ["persona_autorizada_telefono"],
      });
    }

    if (!data.persona_autorizada_direccion || data.persona_autorizada_direccion.trim().length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "legal_address.authorized.address.required",
        path: ["persona_autorizada_direccion"],
      });
    }

    if (!data.persona_autorizada_pais || data.persona_autorizada_pais.trim().length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "legal_address.authorized.country.required",
        path: ["persona_autorizada_pais"],
      });
    }

    if (!data.persona_autorizada_ciudad || data.persona_autorizada_ciudad.trim().length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "legal_address.authorized.city.required",
        path: ["persona_autorizada_ciudad"],
      });
    }
  }
});

export type LegalAddressStepData = z.infer<typeof legalAddressSchema>;

const normalizeLegalAddressData = (
  data?: IncorporationWizardInitialData
): Partial<LegalAddressStepData> => {
  const request = data?.incorporationRequest;
  /**
   *@deprecated - ahora se usa correspondencia_tipo 
   */
  const domicilio_proporcionado =
    request?.domicilio_proporcionado === "cliente" ||
    request?.domicilio_proporcionado === "abogado"
      ? request.domicilio_proporcionado
      : undefined;
  const facturacion =
    request?.facturacion !== undefined ? request.facturacion : true;

  // Determine correspondencia_tipo based on existing data
  // If correspondencia_tipo is already set, use it
  // Otherwise, infer from existing fields:
  // - If there's a physical address (correspondencia_direccion), it's "fisico"
  // - If there's only correspondencia_email, it's "digital"
  // - Default to "digital" if no data exists
  let correspondencia_tipo: "digital" | "fisico" = "digital";
  if (
    request?.correspondencia_tipo === "digital" ||
    request?.correspondencia_tipo === "fisico"
  ) {
    correspondencia_tipo = request.correspondencia_tipo;
  } else if (
    request?.correspondencia_direccion &&
    request.correspondencia_direccion.trim() !== ""
  ) {
    correspondencia_tipo = "fisico";
  } else if (
    request?.correspondencia_email &&
    request.correspondencia_email.trim() !== ""
  ) {
    correspondencia_tipo = "digital";
  }

  return {
    // Base fields (always present)
    persona_autorizada_nombre: request?.persona_autorizada_nombre || "",
    persona_autorizada_email: request?.persona_autorizada_email || "",
    persona_autorizada_telefono: request?.persona_autorizada_telefono || "",
    persona_autorizada_direccion: request?.persona_autorizada_direccion || "",
    persona_autorizada_pais: request?.persona_autorizada_pais || "",
    persona_autorizada_ciudad: request?.persona_autorizada_ciudad || "",
    persona_autorizada_usar_correspondencia:
      request &&
      "persona_autorizada_usar_correspondencia" in request &&
      request.persona_autorizada_usar_correspondencia !== undefined &&
      request.persona_autorizada_usar_correspondencia !== null
        ? request.persona_autorizada_usar_correspondencia
        : correspondencia_tipo === "fisico" ? true : false,
    ...(domicilio_proporcionado && { domicilio_proporcionado }),
    facturacion,
    facturacion_nombre: request?.facturacion_nombre || "",
    facturacion_atencion: request?.facturacion_atencion || "",

    // deprecated -Domicilio fields (always include to keep inputs controlled, for backward compatibility)
    domicilio_direccion: request?.domicilio_direccion || "",
    domicilio_pais: request?.domicilio_pais || "",
    domicilio_ciudad: request?.domicilio_ciudad || "",
    domicilio_telefono: request?.domicilio_telefono || "",

    // Correspondencia fields
    correspondencia_tipo,
    correspondencia_email: request?.correspondencia_email || "",
    correspondencia_nombre: request?.correspondencia_nombre || "",
    correspondencia_direccion: request?.correspondencia_direccion || "",
    correspondencia_pais: request?.correspondencia_pais || "",
    correspondencia_ciudad: request?.correspondencia_ciudad || "",
    correspondencia_telefono: request?.correspondencia_telefono || "",
    correspondencia_persona_contacto:
      request?.correspondencia_persona_contacto || "",
    // Keep correspondencia boolean for backward compatibility
    correspondencia:
      request?.correspondencia !== undefined ? request.correspondencia : true,

    // Facturacion fields (always include to keep inputs controlled)
    facturacion_direccion: request?.facturacion_direccion || "",
    facturacion_pais: request?.facturacion_pais || "",
    facturacion_ciudad: request?.facturacion_ciudad || "",
    facturacion_telefono: request?.facturacion_telefono || "",
  };
};

const LegalAddressStep: StepComponentType = () => {
  const { t } = useTranslation();
  const { data, setData, next } = useIncorporationWizardFormCtx();
  const [saveError, setSaveError] = useState<string | null>(null);

  const {
    control,
    handleSubmit,
    watch,
    getValues,
    setValue,
    setError: setFormError,
    formState: { isSubmitting },
  } = useForm<LegalAddressStepData>({
    resolver: zodResolver(legalAddressSchema),
    defaultValues: normalizeLegalAddressData(data),
  });

  const correspondenciaTipo = watch("correspondencia_tipo");
  const facturacion = watch("facturacion");
  const personaAutorizadaUsarCorrespondencia = watch(
    "persona_autorizada_usar_correspondencia"
  );

  const isCorrespondenciaDigital = correspondenciaTipo === "digital";
  const isCorrespondenciaFisico = correspondenciaTipo === "fisico";
  const isFacturacionUnchecked = facturacion === false;
  const shouldHidePersonaAutorizadaFields =
    isCorrespondenciaFisico && personaAutorizadaUsarCorrespondencia === true;

  // When correspondencia_tipo changes, update correspondencia boolean for backward compatibility
  // and set persona_autorizada_usar_correspondencia to true by default when fisico is selected
  useEffect(() => {
    if (correspondenciaTipo === "digital") {
      setValue("correspondencia", true);
      setValue("persona_autorizada_usar_correspondencia", false);
    } else if (correspondenciaTipo === "fisico") {
      setValue("correspondencia", false);
      // Set checkbox to true by default when fisico is selected (only if not already set)
      const currentValue = getValues("persona_autorizada_usar_correspondencia");
      if (currentValue === undefined || currentValue === false) {
        setValue("persona_autorizada_usar_correspondencia", true);
      }
    }
  }, [correspondenciaTipo, setValue, getValues]);

  // Correspondencia tipo options
  const correspondenciaTipoOptions = [
    {
      value: "digital",
      label: t("legal_address.mail.type.digital"),
    },
    {
      value: "fisico",
      label: t("legal_address.mail.type.fisico"),
    },
  ];

  const onSubmit = async (formData: LegalAddressStepData) => {
    setSaveError(null);

    try {
      // The incorporation request should already exist at this point (created in company-name-step)
      const incorporationRequestId = data?.incorporationRequest?.id;

      if (!incorporationRequestId) {
        throw new Error(
          "La solicitud de incorporación no ha sido creada. Por favor, complete el paso anterior."
        );
      }

      const requestData = {
        ...data?.incorporationRequest,
        ...formData,
      };

      // Call API to update (request should already exist)
      const response = await createOrUpdateIncorporationRequest(requestData);

      // Update local state with response data
      setData((prevData) => {
        if (!prevData) return prevData;
        return {
          ...prevData,
          incorporationRequest: {
            ...prevData.incorporationRequest,
            ...response.data,
            id: response.id,
          },
          incorporationContact: prevData.incorporationContact || [],
          currentUser: prevData.currentUser,
          incorporationStripePrice: prevData.incorporationStripePrice,
          path: prevData.path,
        };
      });

      // Navigate to next step on success
      next();
    } catch (error) {
      // Handle different error types
      if (error instanceof IncorporationRequestError) {
        const errorMessage =
          error.errors && error.errors.length > 0
            ? error.errors.join(", ")
            : error.message;
        setSaveError(errorMessage);

        // For validation errors (422), try to set field-specific errors if possible
        if (error.statusCode === 422 && error.errors) {
          // Set a general form error - could be enhanced to map to specific fields
          setFormError("root", { message: errorMessage });
        }
      } else {
        const errorMessage =
          error instanceof Error ? error.message : "Error al guardar los datos";
        setSaveError(errorMessage);
        setFormError("root", { message: errorMessage });
      }
      // Re-throw to prevent navigation on error
      throw error;
    }
  };

  return (
    <Layout
      title={t("legal_address.title")}
      // description={t("legal_address.description")}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="d-flex flex-column gap-5 text-start mt-4">
          {/* Dirección de Correspondencia Section - Now First */}
          <div className="d-flex flex-column gap-2">
            <h5 className="fw-bold">{t("legal_address.mail.title")}</h5>
            <p className="text-muted">{t("legal_address.mail.description")}</p>
            <RadioGroup
              name="correspondencia_tipo"
              control={control}
              options={correspondenciaTipoOptions}
              hideBorder
              className="w-50"
            />

            {/* Conditional Correspondencia Fields - Digital */}
            {isCorrespondenciaDigital && (
              <div className="d-flex flex-column gap-3 mt-3">
                <TextInput
                  name="correspondencia_email"
                  control={control}
                  label={t("legal_address.mail.email.label")}
                  placeholder={t("legal_address.mail.email.placeholder")}
                  type="email"
                />
              </div>
            )}

            {/* Conditional Correspondencia Fields - Físico */}
            {isCorrespondenciaFisico && (
              <div className="d-flex flex-column gap-3 mt-3">
                <TextInput
                  name="correspondencia_nombre"
                  control={control}
                  label={t("legal_address.mail.name.label")}
                  placeholder={t("legal_address.mail.name.placeholder")}
                />
                <TextInput
                  name="correspondencia_direccion"
                  control={control}
                  label={t("legal_address.mail.address.label")}
                  placeholder={t("legal_address.mail.address.placeholder")}
                />
                <div className="row g-3">
                  <TextInput
                    className="col-12 col-sm-6"
                    name="correspondencia_pais"
                    control={control}
                    label={t("legal_address.mail.country.label")}
                    placeholder={t("legal_address.mail.country.placeholder")}
                  />
                  <TextInput
                    className="col-12 col-sm-6"
                    name="correspondencia_ciudad"
                    control={control}
                    label={t("legal_address.mail.city.label")}
                    placeholder={t("legal_address.mail.city.placeholder")}
                  />
                </div>
                <PhoneInput
                  name="correspondencia_telefono"
                  control={control}
                  label={t("legal_address.mail.phone.label")}
                  placeholder={t("legal_address.mail.phone.placeholder")}
                  initialCountry="pa"
                />
                <TextInput
                  name="correspondencia_persona_contacto"
                  control={control}
                  label={t("legal_address.mail.contact_person.label")}
                  placeholder={t(
                    "legal_address.mail.contact_person.placeholder"
                  )}
                />
              </div>
            )}
          </div>

          {/* Persona Autorizada Section */}
          <div className="d-flex flex-column gap-3">
            <div className="d-flex flex-column">
              <h5 className="fw-bold">{t("legal_address.authorized.title")}</h5>
              <p className="text-muted">
                {t("legal_address.authorized.description")}
              </p>
            </div>
            {/* Checkbox for FÍSICO mode only */}
            {isCorrespondenciaFisico && (
              <CheckboxInput
                name="persona_autorizada_usar_correspondencia"
                control={control}
                label={t("legal_address.authorized.use_same_correspondence")}
              />
            )}
            {/* persona_autorizada_email: always visible & validated */}
            <div className="row g-3">
              {!shouldHidePersonaAutorizadaFields && (
                <TextInput
                  className="col-12 col-sm-6"
                  name="persona_autorizada_nombre"
                  control={control}
                  label={t("legal_address.authorized.name.label")}
                  placeholder={t("legal_address.authorized.name.placeholder")}
                />
              )}
              <TextInput
                className={shouldHidePersonaAutorizadaFields ? "col-12" : "col-12 col-sm-6"}
                name="persona_autorizada_email"
                control={control}
                label={t("legal_address.authorized.email.label")}
                placeholder={t("legal_address.authorized.email.placeholder")}
              />
            </div>
            {/* Other persona autorizada fields - hidden when checkbox is checked in FÍSICO mode */}
            {!shouldHidePersonaAutorizadaFields && (
              <>
                <TextInput
                  name="persona_autorizada_direccion"
                  control={control}
                  label={t("legal_address.authorized.address.label")}
                  placeholder={t("legal_address.authorized.address.placeholder")}
                />
                <div className="row g-3">
                  <TextInput
                    className="col-12 col-sm-6"
                    name="persona_autorizada_pais"
                    control={control}
                    label={t("legal_address.authorized.country.label")}
                    placeholder={t("legal_address.authorized.country.placeholder")}
                  />
                  <TextInput
                    className="col-12 col-sm-6"
                    name="persona_autorizada_ciudad"
                    control={control}
                    label={t("legal_address.authorized.city.label")}
                    placeholder={t("legal_address.authorized.city.placeholder")}
                  />
                </div>
                <PhoneInput
                  name="persona_autorizada_telefono"
                  control={control}
                  label={t("legal_address.authorized.phone.label")}
                  placeholder={t("legal_address.authorized.phone.placeholder")}
                />
              </>
            )}
          </div>

          {/* Datos de Facturación Section */}
          <div className="d-flex flex-column">
            <h5 className="fw-bold">{t("legal_address.billing.title")}</h5>
            <p className="text-muted">
              {t("legal_address.billing.description")}
            </p>

            {/* Checkbox with conditional label based on correspondencia_tipo */}
            <CheckboxInput
              name="facturacion"
              control={control}
              label={
                isCorrespondenciaFisico
                  ? t("legal_address.billing.use_same_correspondence_address")
                  : t("legal_address.billing.use_authorized_person_address")
              }
            />

            {/* Always required billing fields */}
            <div className="row g-3 mt-1">
              <TextInput
                name="facturacion_nombre"
                control={control}
                label={t("legal_address.billing.name.label")}
                // placeholder={t("legal_address.billing.name.placeholder")}
              />
              <TextInput
                name="facturacion_atencion"
                control={control}
                label={t("legal_address.billing.attention.label")}
                placeholder={t("legal_address.billing.attention.placeholder")}
              />
            </div>

            {/* Conditional Billing Address Fields */}
            {isFacturacionUnchecked && (
              <div className="d-flex flex-column gap-3 mt-3">
                <TextInput
                  name="facturacion_direccion"
                  control={control}
                  label={t("legal_address.billing.address.label")}
                  placeholder={t("legal_address.billing.address.placeholder")}
                />
                <div className="row g-3">
                  <TextInput
                    className="col-12 col-sm-6"
                    name="facturacion_pais"
                    control={control}
                    label={t("legal_address.billing.country.label")}
                    placeholder={t("legal_address.billing.country.placeholder")}
                  />
                  <TextInput
                    className="col-12 col-sm-6"
                    name="facturacion_ciudad"
                    control={control}
                    label={t("legal_address.billing.city.label")}
                    placeholder={t("legal_address.billing.city.placeholder")}
                  />
                </div>
                <PhoneInput
                  name="facturacion_telefono"
                  control={control}
                  label={t("legal_address.billing.phone.label")}
                  placeholder={t("legal_address.billing.phone.placeholder")}
                  initialCountry="pa"
                />
              </div>
            )}
          </div>
          {saveError && <div className="alert alert-danger">{saveError}</div>}
          {/* Continue Button */}
          <div className="d-flex justify-content-center">
            <NextButton
              type="submit"
              label={t("legal_address.continue")}
              disabled={isSubmitting}
            />
          </div>
        </div>
      </form>
    </Layout>
  );
};

LegalAddressStep.title = (t) => t("legal_address.title_progress_panel");
LegalAddressStep.description = (t) =>
  t("legal_address.description_progress_panel");
LegalAddressStep.isComplete = (data: IncorporationWizardInitialData) => {
  return legalAddressSchema.safeParse(normalizeLegalAddressData(data)).success;
};

export default LegalAddressStep;
